import { z } from 'zod'
import { calendarDaySchema, type CalendarDay } from '../validators/temporal.validator.js'
import { addDays } from './ad-periods.js'
import { MAX_MILLI, percentOf, type Milli } from './sales-money.js'

/**
 * A payment schedule (docs/sales.spec.md, "Paying"): payments that are each a
 * fixed amount, a percentage, or the rest, each due on signing, on a date, or
 * a set time before the event. The first is the deposit. It always adds up to
 * the total: the rest takes any rounding.
 */

/**
 * Fresh amount members. Called twice so the response copy and the input copy
 * do not share an object: `.strict()` must not be able to tighten what a
 * response reads.
 */
function amountMembers() {
  return [
    z.object({ kind: z.literal('fixed'), cents: z.number().int().min(1, 'A payment needs an amount.').max(100_000_000) }),
    z.object({ kind: z.literal('percent'), milli: z.number().int().min(1, 'A payment needs a percentage.').max(MAX_MILLI) }),
    z.object({ kind: z.literal('rest') }),
  ] as const
}

function dueMembers() {
  return [
    z.object({ kind: z.literal('on_signing') }),
    z.object({ kind: z.literal('before_event'), days: z.number().int().min(0).max(730) }),
    /** Only in a packet, never in a saved schedule: a saved one has no event to date it by. */
    z.object({ kind: z.literal('on_date'), date: calendarDaySchema }),
  ] as const
}

/**
 * What a packet, a booking and a saved schedule read back.
 *
 * Not strict, including inside `amount` and `due`. These objects are embedded
 * in response DTOs, and a response that rejects a key it has not heard of takes
 * the whole page down for every tab that has not reloaded — the login failure
 * of 2026-08-20. `response-shapes.test.ts` only sees `.strict()` written in a
 * dto file, so a strict helper imported from here used to slip past it.
 * Unknown keys are dropped and the payments that were already stored still load.
 */
export const scheduleAmountSchema = z.discriminatedUnion('kind', amountMembers())
export type ScheduleAmount = z.infer<typeof scheduleAmountSchema>

export const scheduleDueSchema = z.discriminatedUnion('kind', dueMembers())
export type ScheduleDue = z.infer<typeof scheduleDueSchema>

export const scheduleEntrySchema = z.object({
  label: z.string().trim().min(1, 'Give each payment a name.').max(60, 'That payment name is too long.'),
  amount: scheduleAmountSchema,
  due: scheduleDueSchema,
})
export type ScheduleEntry = z.infer<typeof scheduleEntrySchema>

/**
 * The same shapes on the way in. A write still refuses a key it does not know,
 * on the entry and inside the amount and the due: a parent `.strict()` does
 * not make a nested object strict.
 */
function strictAmount() {
  const [fixed, percent, rest] = amountMembers()
  return z.discriminatedUnion('kind', [fixed.strict(), percent.strict(), rest.strict()])
}

function strictDue() {
  const [onSigning, beforeEvent, onDate] = dueMembers()
  return z.discriminatedUnion('kind', [onSigning.strict(), beforeEvent.strict(), onDate.strict()])
}

export const scheduleAmountInputSchema = strictAmount()
export const scheduleDueInputSchema = strictDue()
export const scheduleEntryInputSchema = z
  .object({
    label: z.string().trim().min(1, 'Give each payment a name.').max(60, 'That payment name is too long.'),
    amount: scheduleAmountInputSchema,
    due: scheduleDueInputSchema,
  })
  .strict()

export const MAX_SCHEDULE_PAYMENTS = 12

/** Why a schedule can't be used, in plain words; empty when it can. */
export function scheduleErrors(entries: readonly ScheduleEntry[], saved: boolean): string[] {
  const errors: string[] = []
  if (entries.length === 0) errors.push('A schedule needs at least one payment.')
  if (entries.length > MAX_SCHEDULE_PAYMENTS) errors.push(`A schedule can have at most ${String(MAX_SCHEDULE_PAYMENTS)} payments.`)
  const rests = entries.filter((e) => e.amount.kind === 'rest').length
  if (rests !== 1) errors.push('Exactly one payment should be “the rest”, so the schedule always adds up to the total.')
  const percent = entries.reduce((a, e) => a + (e.amount.kind === 'percent' ? e.amount.milli : 0), 0)
  if (percent > MAX_MILLI) errors.push('The percentages add up to more than 100%.')
  if (saved && entries.some((e) => e.due.kind === 'on_date')) errors.push('A saved schedule can’t have a fixed date; use days before the event.')
  return errors
}

export interface ResolvedPayment {
  label: string
  amountCents: number
  /** The day it's due at the venue; null: on signing. */
  dueOn: CalendarDay | null
}

/**
 * A schedule as amounts and days, against a total and the event's date. Fixed
 * amounts and percentages first, then the rest. A payment whose day had
 * already passed by signing (a date, or so many days before the event) is due
 * on signing. `exceeds_total` when the others add up to more than the total.
 */
export function resolveSchedule(
  entries: readonly ScheduleEntry[],
  totalCents: number,
  eventDate: CalendarDay,
  signedOn: CalendarDay | null,
): ResolvedPayment[] | { error: 'exceeds_total' | 'invalid' } {
  if (scheduleErrors(entries, false).length > 0) return { error: 'invalid' }
  const fixed = entries.map((e) => {
    if (e.amount.kind === 'fixed') return e.amount.cents
    if (e.amount.kind === 'percent') return percentOf(totalCents, e.amount.milli)
    return null
  })
  const rest = totalCents - fixed.reduce<number>((a, c) => a + (c ?? 0), 0)
  if (rest < 0) return { error: 'exceeds_total' }
  return entries.map((e, i) => {
    let dueOn: CalendarDay | null = null
    if (e.due.kind !== 'on_signing') {
      const day = e.due.kind === 'on_date' ? e.due.date : addDays(eventDate, -e.due.days)
      dueOn = signedOn !== null && day < signedOn ? null : day
    }
    return { label: e.label, amountCents: fixed[i] ?? rest, dueOn }
  })
}

/** What paying by bank transfer takes: the amount less the venue's discount, which counts as paid. */
export function bankAmount(amountCents: number, discountMilli: Milli): { chargeCents: number; discountCents: number } {
  const discountCents = percentOf(amountCents, discountMilli)
  return { chargeCents: amountCents - discountCents, discountCents }
}

/** A sample of what a schedule comes to, for the settings: against $10,000, an event a year out, signed today. */
export function sampleSchedule(entries: readonly ScheduleEntry[], today: CalendarDay): ReturnType<typeof resolveSchedule> {
  return resolveSchedule(entries, 1_000_000, addDays(today, 365), today)
}
